import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCreateCareerFocus } from '../../hooks/useCareerFocus';
import { useToast } from '../../context/ToastContext';
import { getFocusTypeConfig, FocusSetupField } from '../../constants/focusTypes';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import RetroactiveLinkingModal from './RetroactiveLinkingModal';

interface FocusSetupFlowProps {
  focusType: string;
  isOnboarding?: boolean;
  onComplete?: (focusData?: any) => void;
  onCancel?: () => void;
  allowRetroactive?: boolean;
  initialData?: Record<string, any>;
}

interface StepData {
  [key: string]: any;
}

const FocusSetupFlow: React.FC<FocusSetupFlowProps> = ({
  focusType,
  isOnboarding = false,
  onComplete,
  onCancel,
  allowRetroactive = true,
  initialData = {}
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createFocus, isCreating } = useCreateCareerFocus();
  const { showToast } = useToast();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<StepData>(initialData);
  const [isRetroactive, setIsRetroactive] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showRetroactiveModal, setShowRetroactiveModal] = useState(false);
  const [createdFocusId, setCreatedFocusId] = useState<string | null>(null);
  
  const config = getFocusTypeConfig(focusType);
  
  useEffect(() => {
    if (!config) {
      showToast(`Invalid focus type: ${focusType}`, 'error');
      handleCancel();
    }
  }, [config, focusType]);
  
  if (!config) return null;
  
  // Group fields into steps (max 3 fields per step for better UX)
  const steps = config.setupFields.reduce((acc: FocusSetupField[][], field, index) => {
    const stepIndex = Math.floor(index / 3);
    if (!acc[stepIndex]) acc[stepIndex] = [];
    acc[stepIndex].push(field);
    return acc;
  }, []);
  
  const currentFields = steps[currentStep] || [];
  const isLastStep = currentStep === steps.length - 1;
  const totalSteps = steps.length;
  
  // Check if setup is retroactive
  useEffect(() => {
    if (formData.start_date && allowRetroactive) {
      const startDate = new Date(formData.start_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);
      setIsRetroactive(startDate < today);
    }
  }, [formData.start_date, allowRetroactive]);
  
  const validateField = (field: FocusSetupField, value: any): string | null => {
    if (field.required && !value) {
      return `${field.label} is required`;
    }
    
    if (field.type === 'text_array' && field.max && Array.isArray(value) && value.length > field.max) {
      return `Maximum ${field.max} items allowed`;
    }
    
    if (field.type === 'date') {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      // Validate end date is after start date
      if (field.name === 'end_date' && formData.start_date) {
        const startDate = new Date(formData.start_date);
        if (date <= startDate) {
          return 'End date must be after start date';
        }
      }
    }
    
    return null;
  };
  
  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
    
    currentFields.forEach(field => {
      const error = validateField(field, formData[field.name]);
      if (error) {
        newErrors[field.name] = error;
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    return isValid;
  };
  
  const handleNext = () => {
    if (validateStep()) {
      if (isLastStep) {
        handleSubmit();
      } else {
        setCurrentStep(prev => prev + 1);
        setErrors({});
      }
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setErrors({});
    }
  };
  
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (isOnboarding && onComplete) {
      onComplete(); // Allow skipping in onboarding
    } else {
      navigate(-1);
    }
  };
  
  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    try {
      const focusData = {
        focus_type: focusType as 'internship', // Type assertion for now
        start_date: formData.start_date,
        end_date: formData.end_date || formData.expected_end_date || formData.target_launch_date,
        company: formData.company || formData.institution || formData.project_name,
        role: formData.role || formData.project_title || formData.semester,
        goals: formData.goals || [],
        settings: {
          ...formData.settings,
          setup_retroactively: isRetroactive
        }
      };
      
      const result = await createFocus(focusData);
      
      if (result) {
        setCreatedFocusId(result.id);
        
        if (isRetroactive) {
          // Show retroactive linking modal
          setShowRetroactiveModal(true);
          showToast(
            `${config.label} activated! Let's link your existing cards.`,
            'success'
          );
        } else {
          // Complete immediately if not retroactive
          if (onComplete) {
            onComplete(result);
          } else {
            navigate(`/focus/${focusType}/${result.id}`);
          }
        }
      }
    } catch (error) {
      console.error('Failed to create focus:', error);
      showToast('Failed to create focus. Please try again.', 'error');
    }
  };
  
  const getWeeksAgo = (startDate: string): number => {
    const start = new Date(startDate);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.ceil(diffDays / 7);
  };
  
  const renderField = (field: FocusSetupField) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];
    
    switch (field.type) {
      case 'text':
        return (
          <div key={field.name} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
              placeholder={field.placeholder}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );
        
      case 'date':
        return (
          <div key={field.name} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              value={value}
              onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
            {field.name === 'start_date' && isRetroactive && (
              <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                This date is in the past. We'll help you catch up on your progress.
              </p>
            )}
          </div>
        );
        
      case 'select':
        return (
          <div key={field.name} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              <option value="">Select {field.label}</option>
              {field.options?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );
        
      case 'text_array':
        const items = Array.isArray(value) ? value : [];
        return (
          <div key={field.name} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {items.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[index] = e.target.value;
                      setFormData(prev => ({ ...prev, [field.name]: newItems }));
                    }}
                    placeholder={field.placeholder}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newItems = items.filter((_, i) => i !== index);
                      setFormData(prev => ({ ...prev, [field.name]: newItems }));
                    }}
                    className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
              {(!field.max || items.length < field.max) && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ 
                      ...prev, 
                      [field.name]: [...items, ''] 
                    }));
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  + Add {field.label.replace(/\s*\(.*?\)\s*/g, '').toLowerCase()}
                </button>
              )}
            </div>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-3xl">{config.icon}</span>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isOnboarding ? `Start Your ${config.label}` : `Set Up ${config.label} Focus`}
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          {config.description}
        </p>
      </div>
      
      {/* Progress indicator */}
      {totalSteps > 1 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={`flex-1 h-2 mx-1 rounded-full ${
                  i < currentStep
                    ? 'bg-blue-600'
                    : i === currentStep
                    ? 'bg-blue-400'
                    : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            Step {currentStep + 1} of {totalSteps}
          </p>
        </div>
      )}
      
      {/* Form fields */}
      <div className="mb-8">
        {currentFields.map(renderField)}
      </div>
      
      {/* Quick tips on last step */}
      {isLastStep && config.quickTips.length > 0 && (
        <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
            Quick Tips for Success
          </h3>
          <ul className="space-y-1">
            {config.quickTips.map((tip, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-blue-800 dark:text-blue-200">
                <CheckCircleIcon className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Action buttons */}
      <div className="flex justify-between">
        <button
          type="button"
          onClick={currentStep === 0 ? handleCancel : handleBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
        >
          {currentStep === 0 ? (isOnboarding ? 'Skip for now' : 'Cancel') : 'Back'}
        </button>
        
        <button
          type="button"
          onClick={handleNext}
          disabled={isCreating}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCreating ? 'Creating...' : isLastStep ? 'Complete Setup' : 'Next'}
        </button>
      </div>
      
      {/* Retroactive Linking Modal */}
      {showRetroactiveModal && createdFocusId && (
        <RetroactiveLinkingModal
          isOpen={showRetroactiveModal}
          onClose={() => {
            setShowRetroactiveModal(false);
            if (onComplete) {
              onComplete({ id: createdFocusId });
            } else {
              navigate(`/focus/${focusType}/${createdFocusId}`);
            }
          }}
          focusId={createdFocusId}
          focusType={focusType}
          startDate={formData.start_date}
          endDate={formData.end_date}
        />
      )}
    </div>
  );
};

export default FocusSetupFlow;